import { asMaybeString } from "../../computation-model/data";
import { SerializedComponent } from "../../components/SerializedApp";
import { panic } from "../../Support";
import { makeText } from "../builder-utils";
import {
    ComponentBuilder,
    ComponentConfiguratorContext,
    PropertyDescriptor,
    WireActionHydrator,
    WireInflationBackend,
} from "../components";
import { makeTextPropertyDescriptorWithSpecialValue } from "../components/descriptor-utils";
import {
    ActionDescription,
    ActionKind,
    InputOutputTables,
    PropertyDescription,
    MutatingScreenKind,
} from "../description";
import { ActionDescriptor } from "./action-descriptor";
import { DescriptionToken } from "./action-handler";
import { BaseActionHandler, tokenForProperty } from "./base";

interface CopyToClipboardActionDescription extends ActionDescription {
    readonly data?: PropertyDescription;
}

export function makeDataPropertyDescriptor(
    placeholder: string,
    mutatingScreenKind: MutatingScreenKind | undefined
): PropertyDescriptor {
    return makeTextPropertyDescriptorWithSpecialValue("data", "Data", placeholder, true, mutatingScreenKind, {
        columnFirst: true,
        searchable: false,
    });
}

export class CopyToClipboardWithArgsHandler extends BaseActionHandler<CopyToClipboardActionDescription> {
    public readonly kind = ActionKind.CopyToClipboardWithArgs;
    public readonly iconName = "clipboard";

    public getDescriptor(
        _desc: CopyToClipboardActionDescription | undefined,
        _tables: InputOutputTables | undefined,
        _ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ActionDescriptor {
        return {
            name: "Copy to Clipboard",
            needsScreenContext: true,
            properties: [makeDataPropertyDescriptor("Enter text to copy", mutatingScreenKind)],
        };
    }

    public makeAction(
        builder: ComponentBuilder,
        desc: CopyToClipboardActionDescription,
        tables: InputOutputTables,
        screenContext: SerializedComponent | undefined
    ): SerializedComponent | undefined {
        const { b } = builder;
        if (screenContext === undefined) {
            return panic("Must have screen context for Copy to Clipboard action");
        }

        const copyValue = makeText(builder, desc.data, tables.input, true, true, true, false, screenContext);
        if (copyValue === undefined) return undefined;

        return b.actionIfContextDefined(b.action("CopyToClipboard", { message: copyValue }));
    }

    public getTokenizedDescription(
        desc: CopyToClipboardActionDescription,
        tables: InputOutputTables,
        ccc: ComponentConfiguratorContext
    ): readonly DescriptionToken[] | undefined {
        const columnToken = tokenForProperty(desc.data, tables, ccc);
        if (columnToken === undefined) return undefined;

        return [columnToken];
    }

    public inflate(ib: WireInflationBackend, desc: CopyToClipboardActionDescription): WireActionHydrator | undefined {
        const [valueGetter] = ib.getValueGetterForProperty(desc.data, false);

        return vp => {
            const value = asMaybeString(valueGetter(vp));
            if (value === undefined) return undefined;

            return async ab => {
                ab.actionCallbacks.copyToClipboard(value);
                return true;
            };
        };
    }
}
